import {
  Link,
  PopoverContent,
  PopoverHeader,
  PopoverAnchor,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Popover,
  useDisclosure,
  Box,
  HStack,
  Text,
} from '@chakra-ui/react';
import moment from 'moment';
import { useGetTopDetectionsByDetectionGroupsPerEntity } from '../../../detections/queries';
import { i18n } from '@investigator/shared/localization';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  ROUTES,
  MISSING_DATA_PLACEHOLDER,
} from '@investigator/shared/constants';
import {
  Detection,
  DetectionGroups,
} from '@investigator/shared/generated/graphql';
import { DetectionListSection } from './DetectionListSection';
import { SeverityScore } from '@investigator/shared/components';
import { useEntitiesContext } from '../../context';
import { truncateText } from '@investigator/shared/utils/truncateText';

interface TriageStatusPopoverProps {
  value: { open: number; closed: number };
  entityName: string;
  tenantId: string;
  tenantEntity: string;
  entityCategory: string;
  start?: number;
  end?: number;
}

const { triageStatusPopover } = i18n.pages.entities;

const TRIAGE_DETECTIONS_SHOWN = 3;

// Triage Status Detection Row Component
interface TriageStatusDetectionRowProps {
  detection: Detection;
  index: number;
  formatDate?: (timestamp: number) => string;
  onClick?: (e: React.MouseEvent) => void;
}

const TriageStatusDetectionRow = ({
  detection,
  index,
  formatDate,
  onClick,
}: TriageStatusDetectionRowProps): JSX.Element => {
  return (
    <HStack
      key={`${detection.alert_entity?.entity_name || detection.alert_info?.alert_name}-${index}`}
      py={2}
      px={2}
      width="100%"
      justifyContent="space-between"
      bg="layer.2"
      borderBottom="1px solid"
      borderColor="gray.600"
      _hover={onClick ? { bg: 'layer.3', cursor: 'pointer' } : {}}
      _last={{ borderBottom: 'none' }}
      onClick={onClick}
    >
      <HStack flex="1" minWidth={0}>
        {detection.rank?.severity !== undefined && (
          <SeverityScore score={detection.rank.severity} />
        )}
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {detection.alert_info?.alert_name || MISSING_DATA_PLACEHOLDER}
        </Text>
      </HStack>
      {detection.latest_start_timestamp && formatDate && (
        <Text color="text.secondary" flexShrink={0}>
          {formatDate(detection.latest_start_timestamp)}
        </Text>
      )}
    </HStack>
  );
};

export const TriageStatusPopover = ({
  value,
  entityName,
  tenantId,
  tenantEntity,
  entityCategory,
  start,
  end,
}: TriageStatusPopoverProps): JSX.Element => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { analysisModal } = useEntitiesContext();
  const total = value.closed + value.open;

  const handleViewOpenDetections = () => {
    if (analysisModal.isOpen) {
      analysisModal.onClose();
    }
    window.open(
      ROUTES.detections +
        `?start=${start}&end=${end}&${entityCategory}_name=${entityName}&status=open`,
      '_blank',
    );
  };

  const handleViewClosedDetections = () => {
    if (analysisModal.isOpen) {
      analysisModal.onClose();
    }
    window.open(
      ROUTES.detections +
        `?start=${start}&end=${end}&${entityCategory}_name=${entityName}&status=closed`,
      '_blank',
    );
  };

  // For open detections, show "X days ago"
  const formatOpenDate = (timestamp: number) =>
    moment.unix(timestamp).fromNow();

  // For closed detections, show the actual date
  const formatClosedDate = (timestamp: number) =>
    moment.unix(timestamp).format('MMM D, YYYY');

  const handleDetectionClick = (detection: Detection) => {
    if (analysisModal.isOpen) {
      analysisModal.onClose();
    }
    // Per Mejji, this will open a new tab:
    window.open(
      ROUTES.detectionDetails(
        detection.detection_id,
        `?start=${start}&end=${end}`,
      ),
      '_blank',
    );
  };

  const { data, loading, error } =
    useGetTopDetectionsByDetectionGroupsPerEntity({
      variables: {
        tenant_entity: tenantEntity,
        group_by: DetectionGroups.DetectionStatus,
        tenant: tenantId,
        end: end ?? 0,
        size: TRIAGE_DETECTIONS_SHOWN,
        sort: null,
        start: start ?? 0,
      },
      skip: !isOpen || !start || !end || !tenantEntity || !tenantId,
    });

  // Extract open and closed detections from grouped data
  const openDetections =
    data?.getTopDetectionsByDetectionGroupsPerEntity?.data
      ?.find((group) => group?.group_key === 'open')
      ?.detections?.filter((d): d is Detection => d !== null) || [];

  const closedDetections =
    data?.getTopDetectionsByDetectionGroupsPerEntity?.data
      ?.find((group) => group?.group_key === 'closed')
      ?.detections?.filter((d): d is Detection => d !== null) || [];

  const isLoading = loading;
  const displayEntityName = truncateText(entityName, {
    maxChars: 45,
    shownChars: 20,
  });

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onClose={onClose}
      strategy="fixed"
    >
      <PopoverAnchor>
        <Link
          color="text.link"
          cursor="pointer"
          onClick={(e) => {
            onToggle();
            e.stopPropagation();
          }}
          data-testid="triage-status-popover"
          data-entity-category={entityCategory}
        >
          {`${value.closed}/${total} ${triageStatusPopover.closed}`}
        </Link>
      </PopoverAnchor>
      <PopoverContent
        width="480px"
        zIndex="popover"
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverCloseButton w={5} h={5} fontSize="xs" right={2} top={2} />
        <PopoverHeader fontWeight="bold">
          {`${displayEntityName} | ${triageStatusPopover.title}`}
        </PopoverHeader>
        <PopoverBody>
          {isLoading || error ? (
            <GraphQLReqStatus loading={isLoading} error={error} />
          ) : (
            <>
              {closedDetections.length > 0 && (
                <DetectionListSection
                  title={triageStatusPopover.closedDetections}
                  count={value.closed}
                  detections={closedDetections}
                  buttonText={triageStatusPopover.viewClosedDetections}
                  onViewAllClick={(e) => {
                    e.stopPropagation();
                    handleViewClosedDetections();
                  }}
                  circleColor="gray.500"
                >
                  {(detection, index) => (
                    <TriageStatusDetectionRow
                      detection={detection}
                      index={index}
                      formatDate={formatClosedDate}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetectionClick(detection);
                      }}
                    />
                  )}
                </DetectionListSection>
              )}

              {openDetections.length > 0 && (
                <Box mt={closedDetections.length > 0 ? 6 : 0}>
                  <DetectionListSection
                    title={triageStatusPopover.openDetections}
                    count={value.open}
                    detections={openDetections}
                    buttonText={triageStatusPopover.viewOpenDetections}
                    onViewAllClick={(e) => {
                      e.stopPropagation();
                      handleViewOpenDetections();
                    }}
                    circleColor="green.500"
                  >
                    {(detection, index) => (
                      <TriageStatusDetectionRow
                        detection={detection}
                        index={index}
                        formatDate={formatOpenDate}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDetectionClick(detection);
                        }}
                      />
                    )}
                  </DetectionListSection>
                </Box>
              )}
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
